import Cookies from "js-cookie";
import store from "@utils/redux/store";
import { getURLParams } from "@utils/urlParams";
import { trackEvent } from "@utils/api/tracker";
import { fireFacebookCapi } from "@utils/api/FacebookCapiApi";
import { generateEventId } from "@utils/pixels/utils";
import { shouldAdjustELTV, adjustELTV, extractAdName } from "../eltvAdjustment";
import { registerHandler } from "../handler";

declare const fbq: any;

interface FacebookAdvancedMatchingParams {
  em?: string;
  fn?: string;
  ln?: string;
  email?: string;
  external_id?: string;
}

interface FacebookCustomLeadParams {
  weight?: number;
  idealWeight?: number;
  weightKg?: number;
  idealWeightKg?: number;
  weightLossGoal?: number;
  weightLossGoalKg?: number;
}

const FB_EREV9_PIXEL = "1204952429523059";
const FB_ELTV13_PIXEL = "1557275494440599";
const FB_KR_SANDBOX_PIXEL = "2542854726029439";
const FB_HM_PIXEL = "373833553789964";

function getCustomParams(surveyAnswers) {
  // NOTE(di): We would like to parse and send all survey answers to Facebook.
  const params: FacebookCustomLeadParams = {};

  // NOTE: Facebook has flagged that we shouldn’t be passing the following parameters from the buyflow.
  const notAllowedParamList = [
    "gender",
    "antibiotic",
    "weight",
    "weightKg",
    "pregnant",
    "email",
    "currentHealthRisk",
    "lifeEvent",
    "lifeEventMale",
    "currentHealthRiskMale",
  ];

  if (surveyAnswers) {
    Object.entries(surveyAnswers).forEach(([key, value]) => {
      if (notAllowedParamList.includes(key)) return;
      if (Array.isArray(value)) {
        params[key] = value.join(",");
      } else {
        params[key] = value;
      }
    });
  }

  // NOTE(di): Since Facebook doesn't allow advance calculation on passed values, we pre-calculate some key values here.
  if (params.weight && params.idealWeight) {
    params.weightLossGoal = params.weight - params.idealWeight;
  } else if (params.weightKg && params.idealWeightKg) {
    const weightLossGoalKg = params.weightKg - params.idealWeightKg;
    params.weightLossGoal = Math.round(weightLossGoalKg * 2.2046);
  } else if (params.weightLossGoalKg) {
    params.weightLossGoal = Math.round(params.weightLossGoalKg * 2.2046);
  }

  return params;
}

// NOTE(patrick): I don't like having to cache the URL parameters like this but
// the original conversion tracker caches it in the singleton. It was probably
// due to the url parameters not being successfully passed along from landing to
// payment. Preserve this for now but It would be great to just pull the URL params
// when we need it directly from the URL.
let cachedURLParams = {};

export default function registerHandlers() {
  registerHandler("init", () => {
    // Check to add LDU flag for CCPA opt out.
    if (Cookies.get("ccpaOptOut")) {
      fbq("dataProcessingOptions", ["LDU"], 1, 1000);
    }

    fbq("init", FB_EREV9_PIXEL);
    fbq("init", FB_ELTV13_PIXEL);
    fbq("init", FB_KR_SANDBOX_PIXEL);
    fbq("init", FB_HM_PIXEL);

    fbq("trackSingle", FB_EREV9_PIXEL, "PageView");
    fbq("trackSingle", FB_ELTV13_PIXEL, "PageView");
    fbq("trackSingle", FB_KR_SANDBOX_PIXEL, "PageView");
    fbq("trackSingle", FB_HM_PIXEL, "PageView");

    // Cache the URL Params on page load to preserve tracking params.
    cachedURLParams = getURLParams();
  });

  registerHandler("viewContent", (params) => {
    fbq("trackSingle", FB_EREV9_PIXEL, "ViewContent", params);
    fbq("trackSingle", FB_ELTV13_PIXEL, "ViewContent", params);
  });

  registerHandler("lead", ({ email, name, surveyAnswers }) => {
    // Enables the advance matching feature for Facebook Pixel.
    const { userData } = store.getState();
    const advancedMatchingParams: FacebookAdvancedMatchingParams = {
      ...(userData?.user_id && { external_id: userData.user_id }),
    };

    // NOTE(patrick): We don't collect name until the payment page so this block
    // will probably never get evaluated.
    if (email && email.length > 0 && name && name.length > 0) {
      advancedMatchingParams.em = email.toLowerCase();

      const nameParts = name.trim().split(" ");
      [advancedMatchingParams.fn] = nameParts;
      if (nameParts.length > 1) {
        advancedMatchingParams.ln = nameParts[nameParts.length - 1];
      }
    }

    // NOTE(Patrick): We already do this in the `init` handler but the logic
    // in ConversionTracker fires it twice so leaving it in.
    // Check to add LDU flag for CCPA opt out.
    if (Cookies.get("ccpaOptOut")) {
      fbq("dataProcessingOptions", ["LDU"], 1, 1000);
    }

    fbq("init", FB_EREV9_PIXEL, advancedMatchingParams);
    fbq("init", FB_ELTV13_PIXEL, advancedMatchingParams);
    fbq("init", FB_KR_SANDBOX_PIXEL, advancedMatchingParams);
    fbq("init", FB_HM_PIXEL, advancedMatchingParams);

    // Handle tracking the Lead event.
    const trackLeadParams = getCustomParams(surveyAnswers);
    const fbParams: { value?: number } = {
      ...(surveyAnswers?.weightLossGoal && {
        value: surveyAnswers.weightLossGoal,
      }),
      ...trackLeadParams,
      ...cachedURLParams,
    };

    fbq("trackSingle", FB_EREV9_PIXEL, "Lead", fbParams);
    fbq("trackSingle", FB_ELTV13_PIXEL, "Lead", fbParams);
  });

  registerHandler("initiateCheckout", ({ plan, surveyAnswers }) => {
    const fbParams = {
      value: plan.lv,
      currency: plan.currency ? plan.currency : "USD",
      content_ids: plan.braintree_id,
      content_type: "product",
      content_category: "recurring",
      ...getCustomParams(surveyAnswers),
      ...cachedURLParams,
    };
    fbq("trackSingle", FB_EREV9_PIXEL, "InitiateCheckout", fbParams);
    fbq("trackSingle", FB_ELTV13_PIXEL, "InitiateCheckout", fbParams);
  });

  registerHandler("purchase", ({ email, order, plan, surveyAnswers }) => {
    const { userData, geoLocation } = store.getState();
    const customProperties = {
      ...getCustomParams(surveyAnswers),
      ...cachedURLParams,
    };

    const pixelProperties = {
      value: order.erev_9_months,
      currency: "USD",
      content_ids: plan.braintree_id,
      content_type: "product",
      content_category: "recurring",
      external_id: userData.user_id,
      ...(order.subscriptionId && { order_id: order.subscriptionId }),
      ...customProperties,
    };

    // NOTE(di): For FB algorithm optimization, we need to track both
    // Purchase and StartTrial events with identical properties.
    fbq("trackSingle", FB_EREV9_PIXEL, "StartTrial", pixelProperties);
    fbq("trackSingle", FB_ELTV13_PIXEL, "StartTrial", pixelProperties);

    const eltv13Value = shouldAdjustELTV()
      ? adjustELTV(order.eltv_13_months, geoLocation.country_code)
      : order.eltv_13_months;

    if (eltv13Value !== order.eltv_13_months) {
      trackEvent("AdjustedELTV13", {
        originalValue: order.eltv_13_months,
        adjustedValue: eltv13Value,
        adName: extractAdName(),
      });
    }

    fbq("trackSingle", FB_EREV9_PIXEL, "Purchase", pixelProperties);

    const eventID = generateEventId(userData.user_id);
    const fbParamsELTV13 = {
      ...pixelProperties,
      value: eltv13Value,
    };
    fbq("trackSingle", FB_ELTV13_PIXEL, "Purchase", fbParamsELTV13, {
      eventID,
    });
    fireFacebookCapi(FB_ELTV13_PIXEL, "Purchase", {
      ...fbParamsELTV13,
      customProperties,
      eventID,
      email,
    });
  });
}
